import React, { useState, useEffect, useContext, useMemo, useCallback } from 'react';
import { GlobalContext } from 'context/userContext';
import { DrawerHeader } from '@components/drawer-header';
import {
    Box,
    Paper,
    Typography,
    Autocomplete,
    TextField,
    Button,
    Grid,
    Card,
    CardContent,
    CardHeader,
    CircularProgress,
    Stack,
    IconButton,
    useTheme,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Tooltip,
    Select,
    MenuItem
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import InfoIcon from '@mui/icons-material/Info';
import DeleteIcon from '@mui/icons-material/Delete';
import DownloadIcon from '@mui/icons-material/Download';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getAllTidsStatus } from '../admin-tids/api';
import { getImuSummary, triggerImuRecording, listImuRecords, updateImuConfig, deleteImuRecord } from './api';
import RefreshIcon from '@mui/icons-material/Refresh';
import SearchIcon from '@mui/icons-material/Search';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import CanvasJSReact from '@canvasjs/react-charts';
import { AxiosInstanceLongTimeOut } from '@service/axios-instance';
import DataTable, { TableColumn } from 'react-data-table-component';

const CanvasJSChart = CanvasJSReact.CanvasJSChart;

export function ImuDiagnosticsPage() {
    const theme = useTheme();
    const queryClient = useQueryClient();
    const { operation } = useContext(GlobalContext);

    // Table state
    const [summaryData, setSummaryData] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [filterId, setFilterId] = useState('');
    const [filterTid, setFilterTid] = useState('');
    const [filterSetor, setFilterSetor] = useState('');
    const [filterTipo, setFilterTipo] = useState('');

    // Bulk states
    const [bulkCrashAcc, setBulkCrashAcc] = useState('');
    const [bulkCrashJerk, setBulkCrashJerk] = useState('');
    const [bulkHoleDrop, setBulkHoleDrop] = useState('');
    const [bulkHoleImpact, setBulkHoleImpact] = useState('');
    const [bulkStoppedRms, setBulkStoppedRms] = useState('');
    const [bulkStoppedTime, setBulkStoppedTime] = useState('');
    const [bulkThrImuH, setBulkThrImuH] = useState('');
    const [bulkThrImuV, setBulkThrImuV] = useState('');
    const [bulkSendHole, setBulkSendHole] = useState('');
    const [bulkSendCrash, setBulkSendCrash] = useState('');

    const [changes, setChanges] = useState<Record<string, any>>({});

    // Part 2 state
    const [selectedTid, setSelectedTid] = useState<any>(null);
    const [duration, setDuration] = useState(20);

    const tidsQuery = useQuery({
        queryKey: ['admin-tids'],
        queryFn: getAllTidsStatus,
    });

    const recordsQuery = useQuery({
        queryKey: ['imu-records', selectedTid?.tid],
        queryFn: () => listImuRecords(selectedTid.tid, operation || ""),
        enabled: !!selectedTid,
        refetchInterval: 10000
    });

    const triggerMutation = useMutation({
        mutationFn: () => triggerImuRecording(String(selectedTid.tid), operation || "", duration),
        onSuccess: () => {
            alert("Comando de gravação enviado. Aguarde alguns instantes até o arquivo aparecer.");
            recordsQuery.refetch();
        },
        onError: (e: any) => {
            alert(`Erro ao solicitar gravação: ${e.response?.data?.error || e.message}`);
        }
    });

    const deleteMutation = useMutation({
        mutationFn: (filename: string) => deleteImuRecord(filename, operation || ""),
        onSuccess: () => {
            recordsQuery.refetch();
            loadSummary(); // Refresh counts
        },
        onError: (e: any) => {
            alert(`Erro ao deletar: ${e.response?.data?.error || e.message}`);
        }
    });

    const handleDownload = async (filename: string, e: React.MouseEvent) => {
        e.stopPropagation();
        try {
            const res = await AxiosInstanceLongTimeOut.get(`/imu-diagnostics/download/${filename}?db=${operation || ""}`, {
                responseType: 'blob',
                withCredentials: true
            });
            const url = window.URL.createObjectURL(new Blob([res.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', filename.replace('events/', ''));
            document.body.appendChild(link);
            link.click();
            link.parentNode?.removeChild(link);
            window.URL.revokeObjectURL(url);
        } catch (err) {
            alert("Erro ao baixar o arquivo.");
        }
    };

    const loadSummary = useCallback(async () => {
        if (!operation) return;
        setLoading(true);
        try {
            const data = await getImuSummary(operation || "");
            setSummaryData(data);
            setChanges({});
        } catch (e) {
            console.error(e);
        } finally {
            setLoading(false);
        }
    }, [operation]);

    useEffect(() => {
        loadSummary();
    }, [loadSummary]);

    const handleFieldChange = (deviceId: string, field: string, value: string) => {
        setChanges(prev => ({
            ...prev,
            [deviceId]: {
                ...(prev[deviceId] || {}),
                [field]: value
            }
        }));
    };

    const handleBulkFieldApply = (field: string, value: string) => {
        if (value === "") return;
        const newChanges = { ...changes };
        summaryData.forEach(eq => {
            if (!newChanges[eq.id]) newChanges[eq.id] = {};
            newChanges[eq.id][field] = value;
        });
        setChanges(newChanges);
    };

    const handleSave = async () => {
        setSaving(true);
        try {
            const promises = Object.keys(changes).map(async (deviceId) => {
                const eq = summaryData.find(e => e.id === deviceId);
                if (!eq) return;

                const finalParams = {
                    IMU_CRASH_ACC_G: changes[deviceId].IMU_CRASH_ACC_G ?? eq.IMU_CRASH_ACC_G,
                    IMU_CRASH_JERK: changes[deviceId].IMU_CRASH_JERK ?? eq.IMU_CRASH_JERK,
                    IMU_HOLE_DROP_G: changes[deviceId].IMU_HOLE_DROP_G ?? eq.IMU_HOLE_DROP_G,
                    IMU_HOLE_IMPACT_G: changes[deviceId].IMU_HOLE_IMPACT_G ?? eq.IMU_HOLE_IMPACT_G,
                    IMU_STOPPED_RMS_G: changes[deviceId].IMU_STOPPED_RMS_G ?? eq.IMU_STOPPED_RMS_G,
                    IMU_STOPPED_TIME_S: changes[deviceId].IMU_STOPPED_TIME_S ?? eq.IMU_STOPPED_TIME_S,
                    thr_imu_h: changes[deviceId].thr_imu_h ?? eq.thr_imu_h,
                    thr_imu_v: changes[deviceId].thr_imu_v ?? eq.thr_imu_v,
                    IMU_SEND_HOLE_BUFFER: changes[deviceId].IMU_SEND_HOLE_BUFFER ?? eq.IMU_SEND_HOLE_BUFFER,
                    IMU_SEND_CRASH_BUFFER: changes[deviceId].IMU_SEND_CRASH_BUFFER ?? eq.IMU_SEND_CRASH_BUFFER,
                };

                // convert strings to numbers where appropriate
                const numericParams: any = {};
                for (const [k, v] of Object.entries(finalParams)) {
                    if (v !== "" && v !== null && v !== undefined) {
                        numericParams[k] = Number(v);
                    }
                }

                await updateImuConfig(eq.tid, operation || "", numericParams);
            });

            await Promise.all(promises);
            alert("Parâmetros salvos com sucesso!");
            loadSummary();
        } catch (e: any) {
            alert("Erro ao salvar: " + e.message);
        } finally {
            setSaving(false);
        }
    };

    const columns: TableColumn<any>[] = [
        {
            name: 'ID',
            selector: row => row.id,
            sortable: true,
            minWidth: '80px',
            compact: true,
            center: true
        },
        {
            name: 'TID',
            selector: row => row.tid,
            sortable: true,
            minWidth: '80px',
            compact: true,
            center: true
        },
        {
            name: 'Setor',
            selector: row => row.sector || '',
            sortable: true,
            minWidth: '100px',
            compact: true,
            center: true
        },
        {
            name: 'Tipo',
            selector: row => row.tipo || '',
            sortable: true,
            minWidth: '220px',
            compact: true,
            center: true
        },
        {
            name: 'Manual Recs',
            selector: row => row.manual_records,
            sortable: true,
            minWidth: '80px',
            compact: true,
            center: true
        },
        {
            name: 'Crash Recs',
            selector: row => row.crash_records,
            sortable: true,
            minWidth: '80px',
            compact: true,
            center: true
        },
        {
            name: 'Hole Recs',
            selector: row => row.hole_records,
            sortable: true,
            minWidth: '80px',
            compact: true,
            center: true
        },
        {
            name: (
                <Stack spacing={1} sx={{ width: '100%', py: 1 }} alignItems="center">
                    <Typography variant="subtitle2" sx={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}>Crash Acc (G)</Typography>
                    <TextField size="small" fullWidth type="number" value={bulkCrashAcc}
                        onChange={(e) => { setBulkCrashAcc(e.target.value); handleBulkFieldApply('IMU_CRASH_ACC_G', e.target.value); }}
                        sx={{ height: 35, '& .MuiInputBase-root': { height: 35, fontSize: '0.875rem' } }} />
                </Stack>
            ),
            cell: (row) => (
                <TextField size="small" fullWidth type="number"
                    value={changes[row.id]?.IMU_CRASH_ACC_G !== undefined ? changes[row.id].IMU_CRASH_ACC_G : (row.IMU_CRASH_ACC_G ?? '')}
                    onChange={(e) => handleFieldChange(row.id, 'IMU_CRASH_ACC_G', e.target.value)} placeholder="3.0"
                    sx={{ height: 35, '& .MuiInputBase-root': { height: 35, fontSize: '0.875rem' } }} />
            ),
            minWidth: '150px',
            center: true
        },
        {
            name: (
                <Stack spacing={1} sx={{ width: '100%', py: 1 }} alignItems="center">
                    <Typography variant="subtitle2" sx={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}>Crash Jerk</Typography>
                    <TextField size="small" fullWidth type="number" value={bulkCrashJerk}
                        onChange={(e) => { setBulkCrashJerk(e.target.value); handleBulkFieldApply('IMU_CRASH_JERK', e.target.value); }}
                        sx={{ height: 35, '& .MuiInputBase-root': { height: 35, fontSize: '0.875rem' } }} />
                </Stack>
            ),
            cell: (row) => (
                <TextField size="small" fullWidth type="number"
                    value={changes[row.id]?.IMU_CRASH_JERK !== undefined ? changes[row.id].IMU_CRASH_JERK : (row.IMU_CRASH_JERK ?? '')}
                    onChange={(e) => handleFieldChange(row.id, 'IMU_CRASH_JERK', e.target.value)} placeholder="50.0"
                    sx={{ height: 35, '& .MuiInputBase-root': { height: 35, fontSize: '0.875rem' } }} />
            ),
            minWidth: '150px',
            center: true
        },
        {
            name: (
                <Stack spacing={1} sx={{ width: '100%', py: 1 }} alignItems="center">
                    <Typography variant="subtitle2" sx={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}>Hole Drop (G)</Typography>
                    <TextField size="small" fullWidth type="number" value={bulkHoleDrop}
                        onChange={(e) => { setBulkHoleDrop(e.target.value); handleBulkFieldApply('IMU_HOLE_DROP_G', e.target.value); }}
                        sx={{ height: 35, '& .MuiInputBase-root': { height: 35, fontSize: '0.875rem' } }} />
                </Stack>
            ),
            cell: (row) => (
                <TextField size="small" fullWidth type="number"
                    value={changes[row.id]?.IMU_HOLE_DROP_G !== undefined ? changes[row.id].IMU_HOLE_DROP_G : (row.IMU_HOLE_DROP_G ?? '')}
                    onChange={(e) => handleFieldChange(row.id, 'IMU_HOLE_DROP_G', e.target.value)} placeholder="0.2"
                    sx={{ height: 35, '& .MuiInputBase-root': { height: 35, fontSize: '0.875rem' } }} />
            ),
            minWidth: '150px',
            center: true
        },
        {
            name: (
                <Stack spacing={1} sx={{ width: '100%', py: 1 }} alignItems="center">
                    <Typography variant="subtitle2" sx={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}>Hole Impact (G)</Typography>
                    <TextField size="small" fullWidth type="number" value={bulkHoleImpact}
                        onChange={(e) => { setBulkHoleImpact(e.target.value); handleBulkFieldApply('IMU_HOLE_IMPACT_G', e.target.value); }}
                        sx={{ height: 35, '& .MuiInputBase-root': { height: 35, fontSize: '0.875rem' } }} />
                </Stack>
            ),
            cell: (row) => (
                <TextField size="small" fullWidth type="number"
                    value={changes[row.id]?.IMU_HOLE_IMPACT_G !== undefined ? changes[row.id].IMU_HOLE_IMPACT_G : (row.IMU_HOLE_IMPACT_G ?? '')}
                    onChange={(e) => handleFieldChange(row.id, 'IMU_HOLE_IMPACT_G', e.target.value)} placeholder="3.5"
                    sx={{ height: 35, '& .MuiInputBase-root': { height: 35, fontSize: '0.875rem' } }} />
            ),
            minWidth: '150px',
            center: true
        },
        {
            name: (
                <Stack spacing={1} sx={{ width: '100%', py: 1 }} alignItems="center">
                    <Typography variant="subtitle2" sx={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}>Stopped RMS (G)</Typography>
                    <TextField size="small" fullWidth type="number" value={bulkStoppedRms}
                        onChange={(e) => { setBulkStoppedRms(e.target.value); handleBulkFieldApply('IMU_STOPPED_RMS_G', e.target.value); }}
                        sx={{ height: 35, '& .MuiInputBase-root': { height: 35, fontSize: '0.875rem' } }} />
                </Stack>
            ),
            cell: (row) => (
                <TextField size="small" fullWidth type="number"
                    value={changes[row.id]?.IMU_STOPPED_RMS_G !== undefined ? changes[row.id].IMU_STOPPED_RMS_G : (row.IMU_STOPPED_RMS_G ?? '')}
                    onChange={(e) => handleFieldChange(row.id, 'IMU_STOPPED_RMS_G', e.target.value)} placeholder="0.02"
                    sx={{ height: 35, '& .MuiInputBase-root': { height: 35, fontSize: '0.875rem' } }} />
            ),
            minWidth: '150px',
            center: true
        },
        {
            name: (
                <Stack spacing={1} sx={{ width: '100%', py: 1 }} alignItems="center">
                    <Typography variant="subtitle2" sx={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}>Stopped Time (s)</Typography>
                    <TextField size="small" fullWidth type="number" value={bulkStoppedTime}
                        onChange={(e) => { setBulkStoppedTime(e.target.value); handleBulkFieldApply('IMU_STOPPED_TIME_S', e.target.value); }}
                        sx={{ height: 35, '& .MuiInputBase-root': { height: 35, fontSize: '0.875rem' } }} />
                </Stack>
            ),
            cell: (row) => (
                <TextField size="small" fullWidth type="number"
                    value={changes[row.id]?.IMU_STOPPED_TIME_S !== undefined ? changes[row.id].IMU_STOPPED_TIME_S : (row.IMU_STOPPED_TIME_S ?? '')}
                    onChange={(e) => handleFieldChange(row.id, 'IMU_STOPPED_TIME_S', e.target.value)} placeholder="5"
                    sx={{ height: 35, '& .MuiInputBase-root': { height: 35, fontSize: '0.875rem' } }} />
            ),
            minWidth: '150px',
            center: true
        },
        {
            name: (
                <Stack spacing={1} sx={{ width: '100%', py: 1 }} alignItems="center">
                    <Typography variant="subtitle2" sx={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}>Send Hole Buffer</Typography>
                    <Select size="small" fullWidth displayEmpty value={bulkSendHole}
                        onChange={(e) => { setBulkSendHole(e.target.value as string); handleBulkFieldApply('IMU_SEND_HOLE_BUFFER', e.target.value as string); }}
                        sx={{ height: 35, fontSize: '0.875rem' }}>
                        <MenuItem value="">-</MenuItem><MenuItem value="1">1 (Sim)</MenuItem><MenuItem value="0">0 (Não)</MenuItem>
                    </Select>
                </Stack>
            ),
            cell: (row) => (
                <Select size="small" fullWidth displayEmpty
                    value={changes[row.id]?.IMU_SEND_HOLE_BUFFER !== undefined ? String(changes[row.id].IMU_SEND_HOLE_BUFFER) : (row.IMU_SEND_HOLE_BUFFER !== "" && row.IMU_SEND_HOLE_BUFFER !== undefined && row.IMU_SEND_HOLE_BUFFER !== null ? String(row.IMU_SEND_HOLE_BUFFER) : "")}
                    onChange={(e) => handleFieldChange(row.id, 'IMU_SEND_HOLE_BUFFER', e.target.value as string)}
                    sx={{ height: 35, fontSize: '0.875rem' }}>
                    <MenuItem value="">-</MenuItem><MenuItem value="1">1 (Sim)</MenuItem><MenuItem value="0">0 (Não)</MenuItem>
                </Select>
            ),
            minWidth: '160px',
            center: true
        },
        {
            name: (
                <Stack spacing={1} sx={{ width: '100%', py: 1 }} alignItems="center">
                    <Typography variant="subtitle2" sx={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}>Send Crash Buffer</Typography>
                    <Select size="small" fullWidth displayEmpty value={bulkSendCrash}
                        onChange={(e) => { setBulkSendCrash(e.target.value as string); handleBulkFieldApply('IMU_SEND_CRASH_BUFFER', e.target.value as string); }}
                        sx={{ height: 35, fontSize: '0.875rem' }}>
                        <MenuItem value="">-</MenuItem><MenuItem value="1">1 (Sim)</MenuItem><MenuItem value="0">0 (Não)</MenuItem>
                    </Select>
                </Stack>
            ),
            cell: (row) => (
                <Select size="small" fullWidth displayEmpty
                    value={changes[row.id]?.IMU_SEND_CRASH_BUFFER !== undefined ? String(changes[row.id].IMU_SEND_CRASH_BUFFER) : (row.IMU_SEND_CRASH_BUFFER !== "" && row.IMU_SEND_CRASH_BUFFER !== undefined && row.IMU_SEND_CRASH_BUFFER !== null ? String(row.IMU_SEND_CRASH_BUFFER) : "")}
                    onChange={(e) => handleFieldChange(row.id, 'IMU_SEND_CRASH_BUFFER', e.target.value as string)}
                    sx={{ height: 35, fontSize: '0.875rem' }}>
                    <MenuItem value="">-</MenuItem><MenuItem value="1">1 (Sim)</MenuItem><MenuItem value="0">0 (Não)</MenuItem>
                </Select>
            ),
            minWidth: '160px',
            center: true
        }
    ];

    const hasChanges = Object.keys(changes).length > 0;

    const handleRowClicked = (row: any) => {
        const option = tidsQuery.data?.find((o: any) => String(o.tid) === String(row.tid));
        if (option) {
            setSelectedTid(option);
            setTimeout(() => {
                window.scrollTo({ top: document.body.scrollHeight, behavior: 'smooth' });
            }, 100);
        } else {
            console.warn("TID not found in tidsQuery:", row.tid);
        }
    };

    
    const filteredData = summaryData.filter(
        item => 
            (!filterId || (item.id && String(item.id).toLowerCase().includes(filterId.toLowerCase()))) &&
            (!filterTid || (item.tid && String(item.tid).toLowerCase().includes(filterTid.toLowerCase()))) &&
            (!filterSetor || (item.sector && String(item.sector).toLowerCase().includes(filterSetor.toLowerCase()))) &&
            (!filterTipo || (item.tipo && String(item.tipo).toLowerCase().includes(filterTipo.toLowerCase())))
    );

    return (
        <DrawerHeader title="Diagnóstico de IMU Remoto">
            <Box sx={{ mb: 3 }}>
                <Paper elevation={2} sx={{ p: { xs: 2.5, md: 3 }, bgcolor: 'background.paper', borderLeft: `6px solid ${theme.palette.primary.main}`, borderRadius: 2 }}>
                    <Typography variant="overline" color="text.secondary" sx={{ letterSpacing: 1.5, fontWeight: 600 }}>AUTOMNI STAFF</Typography>
                    <Typography variant="h4" fontFamily="Saira" fontWeight={900} color="text.primary">Análise de Acelerômetro</Typography>
                    <Typography variant="body1" color="text.secondary" sx={{ mt: 1 }}>
                        Ajuste os parâmetros de detecção de eventos e visualize dados RAW do IMU das máquinas remotas.
                    </Typography>
                </Paper>
            </Box>

            <Grid container spacing={3}>
                <Grid item xs={12}>
                    <Card>
                        <CardHeader
                            title="1. Parâmetros de IMU das Máquinas"
                            action={
                                <Stack direction="row" spacing={2}>
                                    <IconButton onClick={loadSummary} disabled={loading}><RefreshIcon /></IconButton>
                                    <Button variant="contained" color="primary" disabled={!hasChanges || saving} onClick={handleSave}>
                                        {saving ? <CircularProgress size={24} /> : 'Salvar Alterações'}
                                    </Button>
                                </Stack>
                            }
                        />
                        <CardContent>
                            <Grid container spacing={2} sx={{ mb: 2, maxWidth: 600 }}>
                                <Grid item xs={3}>
                                    <TextField size="small" fullWidth placeholder="Filtrar ID" value={filterId} onChange={e => setFilterId(e.target.value)} />
                                </Grid>
                                <Grid item xs={3}>
                                    <TextField size="small" fullWidth placeholder="Filtrar TID" value={filterTid} onChange={e => setFilterTid(e.target.value)} />
                                </Grid>
                                <Grid item xs={3}>
                                    <TextField size="small" fullWidth placeholder="Filtrar Setor" value={filterSetor} onChange={e => setFilterSetor(e.target.value)} />
                                </Grid>
                                <Grid item xs={3}>
                                    <TextField size="small" fullWidth placeholder="Filtrar Tipo" value={filterTipo} onChange={e => setFilterTipo(e.target.value)} />
                                </Grid>
                            </Grid>
                            <DataTable
                                columns={columns}
                                data={filteredData}
                                progressPending={loading}
                                pagination
                                paginationPerPage={50}
                                paginationRowsPerPageOptions={[10, 50, 100, 500]}
                                pointerOnHover
                                highlightOnHover
                                onRowClicked={handleRowClicked}
                                customStyles={{
                                    headCells: { 
                                        style: { 
                                            backgroundColor: '#f5f5f5', 
                                            fontWeight: 'bold',
                                            '&:nth-of-type(7)': { borderRight: '1px solid #ccc' },
                                            '&:nth-of-type(9)': { borderRight: '1px solid #ccc' },
                                            '&:nth-of-type(11)': { borderRight: '1px solid #ccc' },
                                            '&:nth-of-type(13)': { borderRight: '1px solid #ccc' },
                                        } 
                                    },
                                    cells: { 
                                        style: { 
                                            padding: '8px',
                                            '&:nth-of-type(7)': { borderRight: '1px solid #ccc' },
                                            '&:nth-of-type(9)': { borderRight: '1px solid #ccc' },
                                            '&:nth-of-type(11)': { borderRight: '1px solid #ccc' },
                                            '&:nth-of-type(13)': { borderRight: '1px solid #ccc' },
                                        } 
                                    }
                                }}
                            />
                        </CardContent>
                    </Card>
                </Grid>

                <Grid item xs={12}>
                    <Card>
                        <CardHeader title="2. Análise de Gravações RAW" />
                        <CardContent>
                            <Grid container spacing={3}>
                                <Grid item xs={12}>
                                    <Stack direction={{ xs: 'column', md: 'row' }} spacing={3} alignItems="flex-end">
                                        <Box flex={1}>
                                            <Typography variant="subtitle1" gutterBottom>Selecione um equipamento para analisar gravações</Typography>
                                            {tidsQuery.isLoading ? (
                                                <CircularProgress size={24} />
                                            ) : (
                                                <Autocomplete
                                                    options={tidsQuery.data || []}
                                                    getOptionLabel={(option: any) => `${option.tid} - ${option.sector || option.machine_id || 'Sem nome'} (${option.operation})`}
                                                    value={selectedTid}
                                                    onChange={(_, newValue) => setSelectedTid(newValue)}
                                                    renderInput={(params) => <TextField {...params} label="Buscar TID / Máquina" variant="outlined" />}
                                                    isOptionEqualToValue={(o, v) => o.tid === v.tid}
                                                />
                                            )}
                                        </Box>

                                        {selectedTid && (
                                            <Box>
                                                <Typography variant="subtitle2" gutterBottom>Solicitar Gravação Manual RAW</Typography>
                                                <Stack direction="row" spacing={2} alignItems="center">
                                                    <TextField
                                                        label="Duração (seg)"
                                                        type="number"
                                                        value={duration}
                                                        onChange={(e) => setDuration(Number(e.target.value))}
                                                        size="small"
                                                        sx={{ width: 120 }}
                                                    />
                                                    <Button
                                                        variant="contained"
                                                        color="primary"
                                                        startIcon={<PlayArrowIcon />}
                                                        onClick={() => triggerMutation.mutate()}
                                                        disabled={triggerMutation.isPending}
                                                    >
                                                        {triggerMutation.isPending ? 'Enviando...' : 'Gravar'}
                                                    </Button>
                                                </Stack>
                                            </Box>
                                        )}
                                    </Stack>
                                </Grid>

                                <Grid item xs={12}>
                                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={2} mt={2}>
                                        <Typography variant="h6">Arquivos do Equipamento</Typography>
                                        <IconButton onClick={() => recordsQuery.refetch()} disabled={!selectedTid}>
                                            <RefreshIcon />
                                        </IconButton>
                                    </Box>

                                    {!selectedTid ? (
                                        <Typography color="text.secondary">Selecione um equipamento acima.</Typography>
                                    ) : recordsQuery.isLoading ? (
                                        <CircularProgress />
                                    ) : recordsQuery.data?.length === 0 ? (
                                        <Typography color="text.secondary">Nenhum arquivo RAW encontrado para este TID.</Typography>
                                    ) : (
                                        <Box>
                                            {recordsQuery.data?.map((fileInfo: any) => (
                                                <Accordion key={fileInfo.filename} TransitionProps={{ unmountOnExit: true }}>
                                                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                                        <Box display="flex" justifyContent="space-between" alignItems="center" width="100%" pr={2}>
                                                            <Typography fontWeight={500}>
                                                                {fileInfo.filename.replace('events/', '')} | {fileInfo.type} | {new Date(fileInfo.timestamp * 1000).toLocaleString('pt-BR')}
                                                            </Typography>
                                                            <Stack direction="row" spacing={2} alignItems="center">
                                                                <Typography variant="body2" color="text.secondary">{(fileInfo.size / 1024).toFixed(1)} KB</Typography>
                                                                <IconButton size="small" color="primary" onClick={(e) => handleDownload(fileInfo.filename, e)}>
                                                                    <DownloadIcon fontSize="small" />
                                                                </IconButton>
                                                                <IconButton size="small" color="error" onClick={(e) => { e.stopPropagation(); deleteMutation.mutate(fileInfo.filename); }}>
                                                                    <DeleteIcon fontSize="small" />
                                                                </IconButton>
                                                            </Stack>
                                                        </Box>
                                                    </AccordionSummary>
                                                    <AccordionDetails>
                                                        <ImuChartViewer fileInfo={fileInfo} db={operation || ""} params={summaryData.find((e: any) => String(e.tid) === String(selectedTid?.tid)) || {}} />
                                                    </AccordionDetails>
                                                </Accordion>
                                            ))}
                                        </Box>
                                    )}
                                </Grid>
                            </Grid>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>
        </DrawerHeader>
    );
}

function ImuChartViewer({ fileInfo, db, params }: { fileInfo: any, db: string, params: any }) {
    const [chartOptions, setChartOptions] = useState<any>(null);
    const [metrics, setMetrics] = useState<any>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    const loadData = async () => {
        setLoading(true);
        setError('');
        try {
            const res = await AxiosInstanceLongTimeOut.get(`/imu-diagnostics/download/${fileInfo.filename}?db=${db}`, {
                responseType: 'arraybuffer',
                withCredentials: true
            });

            const buffer = res.data;
            const dataView = new DataView(buffer);

            const ACC_SCALE = (1.0 / 4096.0) * 9.81;
            const GYRO_SCALE = 1.0 / 16.4;

            const N = Math.floor(buffer.byteLength / 12);
            const dpsAccX: any[] = [], dpsAccY: any[] = [], dpsAccZ: any[] = [];
            const dpsMag: any[] = [];
            const dpsGyroX: any[] = [], dpsGyroY: any[] = [], dpsGyroZ: any[] = [];

            const dpsAccVert: any[] = [];
            const dpsAccHoriz: any[] = [];
            const dpsJerkH: any[] = [];
            const crashMarkers: any[] = [];
            const holeMarkers: any[] = [];

            let maxMag = 0;
            let sumMag = 0;
            let sumSqMag = 0;
            const magArray = new Float32Array(N);

            const kGravity = 9.81;
            const kSampleRateHz = 400;
            const kWarmupAlpha = 0.08;
            const kRunAlpha = 0.005;
            const g_est = [0.0, 0.0, 1.0];
            let stable_samples = 0;
            let armed = false;
            let prev_acc_horiz = 0;
            let hole_candidate = false;
            let hole_timer = 0;
            let last_event_idx = -10000;
            const kDebounceEventSamples = 5000 * 400 / 1000; // 5000ms

            const p_crash_acc = (params.IMU_CRASH_ACC_G || 2.0) * kGravity;
            const p_crash_jerk = params.IMU_CRASH_JERK || 500.0;
            const p_hole_drop = (params.IMU_HOLE_DROP_G || 0.4) * kGravity;
            const p_hole_impact = (params.IMU_HOLE_IMPACT_G || 2.0) * kGravity;

            for (let i = 0; i < N; i++) {
                const offset = i * 12;
                const accX = dataView.getInt16(offset, false) * ACC_SCALE;
                const accY = dataView.getInt16(offset + 2, false) * ACC_SCALE;
                const accZ = dataView.getInt16(offset + 4, false) * ACC_SCALE;
                const gyroX = dataView.getInt16(offset + 6, false) * GYRO_SCALE;
                const gyroY = dataView.getInt16(offset + 8, false) * GYRO_SCALE;
                const gyroZ = dataView.getInt16(offset + 10, false) * GYRO_SCALE;

                const mag = Math.sqrt(accX * accX + accY * accY + accZ * accZ);
                if (mag > maxMag) maxMag = mag;
                sumMag += mag;
                sumSqMag += mag * mag;
                magArray[i] = mag;

                // Event Filter Simulation
                const stable_sample = mag > (0.5 * kGravity) && mag < (1.5 * kGravity);
                const was_armed = armed;

                if (stable_sample) {
                    const alpha = was_armed ? kRunAlpha : kWarmupAlpha;
                    g_est[0] = g_est[0] * (1.0 - alpha) + accX * alpha;
                    g_est[1] = g_est[1] * (1.0 - alpha) + accY * alpha;
                    g_est[2] = g_est[2] * (1.0 - alpha) + accZ * alpha;
                    if (!was_armed) {
                        stable_samples++;
                        if (stable_samples >= 100) armed = true;
                    }
                } else if (!was_armed) {
                    stable_samples = 0;
                    hole_candidate = false;
                }

                const g_len = Math.sqrt(g_est[0] * g_est[0] + g_est[1] * g_est[1] + g_est[2] * g_est[2]);
                let g_norm = [0, 0, 1];
                if (g_len > 0.001) {
                    g_norm = [g_est[0] / g_len, g_est[1] / g_len, g_est[2] / g_len];
                }

                const acc_vert = accX * g_norm[0] + accY * g_norm[1] + accZ * g_norm[2];
                let acc_horiz_sq = (mag * mag) - (acc_vert * acc_vert);
                if (acc_horiz_sq < 0) acc_horiz_sq = 0;
                const acc_horiz = Math.sqrt(acc_horiz_sq);

                let jerk_h = 0;

                if (armed) {
                    if (acc_horiz > p_crash_acc) {
                        jerk_h = (acc_horiz - prev_acc_horiz) * kSampleRateHz;
                        prev_acc_horiz = acc_horiz;

                        if (Math.abs(jerk_h) > p_crash_jerk) {
                            if (i - last_event_idx > kDebounceEventSamples) {
                                crashMarkers.push({ x: i, y: acc_horiz, indexLabel: "CRASH", markerColor: "red", markerType: "cross" });
                                last_event_idx = i;
                                hole_candidate = false;
                            }
                        }
                    } else {
                        prev_acc_horiz = acc_horiz;
                    }

                    if (mag < p_hole_drop) {
                        hole_candidate = true;
                        hole_timer = i;
                    }

                    if (hole_candidate) {
                        if (i - hole_timer > (200 * 400 / 1000)) {
                            hole_candidate = false;
                        } else if (acc_vert > p_hole_impact) {
                            if (i - last_event_idx > kDebounceEventSamples) {
                                holeMarkers.push({ x: i, y: acc_vert, indexLabel: "HOLE", markerColor: "orange", markerType: "triangle" });
                                last_event_idx = i;
                            }
                        }
                    }
                }

                dpsAccX.push({ x: i, y: accX });
                dpsAccY.push({ x: i, y: accY });
                dpsAccZ.push({ x: i, y: accZ });
                dpsMag.push({ x: i, y: mag });
                dpsGyroX.push({ x: i, y: gyroX });
                dpsGyroY.push({ x: i, y: gyroY });
                dpsGyroZ.push({ x: i, y: gyroZ });
                dpsAccVert.push({ x: i, y: acc_vert });
                dpsAccHoriz.push({ x: i, y: acc_horiz });
                dpsJerkH.push({ x: i, y: jerk_h });
            }

            const meanMag = sumMag / N;
            const variance = (sumSqMag / N) - (meanMag * meanMag);
            const stdDev = Math.sqrt(variance);
            const rms = Math.sqrt(sumSqMag / N);
            const crestFactor = maxMag / rms;

            let sumFourth = 0;
            for (let i = 0; i < N; i++) {
                sumFourth += Math.pow(magArray[i] - meanMag, 4);
            }
            const kurtosis = (sumFourth / N) / Math.pow(variance, 2);

            setMetrics({ maxMag, meanMag, stdDev, rms, crestFactor, kurtosis });

            setChartOptions({
                acc: {
                    animationEnabled: true,
                    zoomEnabled: true,
                    title: { text: "Acelerômetro (m/s²)", fontSize: 16 },
                    axisX: { title: "Amostras" },
                    toolTip: { shared: true },
                    data: [
                        { type: "line", name: "X", showInLegend: true, dataPoints: dpsAccX, color: "#e6194b" },
                        { type: "line", name: "Y", showInLegend: true, dataPoints: dpsAccY, color: "#3cb44b" },
                        { type: "line", name: "Z", showInLegend: true, dataPoints: dpsAccZ, color: "#4363d8" }
                    ]
                },
                magHist: {
                    animationEnabled: true,
                    title: { text: "Distribuição de Magnitude", fontSize: 16 },
                    axisX: { title: "Magnitude (m/s²)" },
                    axisY: { title: "Ocorrências" },
                    data: [{
                        type: "column",
                        dataPoints: createHistogram(magArray, 20)
                    }]
                },
                gyro: {
                    animationEnabled: true,
                    zoomEnabled: true,
                    title: { text: "Giroscópio (rad/s)", fontSize: 16 },
                    axisX: { title: "Amostras" },
                    toolTip: { shared: true },
                    data: [
                        { type: "line", name: "X", showInLegend: true, dataPoints: dpsGyroX, color: "#f58231" },
                        { type: "line", name: "Y", showInLegend: true, dataPoints: dpsGyroY, color: "#f032e6" },
                        { type: "line", name: "Z", showInLegend: true, dataPoints: dpsGyroZ, color: "#800000" }
                    ]
                },
                telemetry: {
                    animationEnabled: true,
                    zoomEnabled: true,
                    title: { text: "Filtro de Eventos (Telemetria)", fontSize: 16 },
                    axisX: { title: "Amostras" },
                    axisY: { title: "Aceleração (m/s²)" },
                    axisY2: { title: "Jerk H (m/s³)" },
                    toolTip: { shared: true },
                    data: [
                        { type: "line", name: "Acc Horiz (m/s²)", showInLegend: true, dataPoints: dpsAccHoriz, color: "#1f77b4" },
                        { type: "line", name: "Acc Vert (m/s²)", showInLegend: true, dataPoints: dpsAccVert, color: "#ff7f0e" },
                        { type: "line", name: "Jerk H (m/s³)", showInLegend: true, dataPoints: dpsJerkH, color: "#2ca02c", axisYType: "secondary" },
                        { type: "scatter", name: "Crash", showInLegend: true, dataPoints: crashMarkers, color: "red", markerSize: 15 },
                        { type: "scatter", name: "Hole", showInLegend: true, dataPoints: holeMarkers, color: "orange", markerSize: 15 }
                    ]
                }
            });

        } catch (err: any) {
            setError(err.message || 'Falha ao carregar arquivo');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadData();
    }, [fileInfo]);

    if (loading) return <Box p={2}><CircularProgress /></Box>;
    if (error) return <Typography color="error">{error}</Typography>;
    if (!chartOptions) return null;

    return (
        <Box>
            {metrics && (
                <Grid container spacing={2} mb={3}>
                    <Grid item xs={6} sm={4} md={2}>
                        <Paper elevation={1} sx={{ p: 1.5, textAlign: 'center', bgcolor: 'background.default' }}>
                            <Typography variant="caption" color="text.secondary" fontWeight={600}>Max Mag</Typography>
                            <Typography variant="h6" color="primary.main">{metrics.maxMag.toFixed(2)}</Typography>
                        </Paper>
                    </Grid>
                    <Grid item xs={6} sm={4} md={2}>
                        <Paper elevation={1} sx={{ p: 1.5, textAlign: 'center', bgcolor: 'background.default' }}>
                            <Typography variant="caption" color="text.secondary" fontWeight={600}>Mean Mag</Typography>
                            <Typography variant="h6" color="primary.main">{metrics.meanMag.toFixed(2)}</Typography>
                        </Paper>
                    </Grid>
                    <Grid item xs={6} sm={4} md={2}>
                        <Paper elevation={1} sx={{ p: 1.5, textAlign: 'center', bgcolor: 'background.default' }}>
                            <Typography variant="caption" color="text.secondary" fontWeight={600}>Std Dev</Typography>
                            <Typography variant="h6" color="primary.main">{metrics.stdDev.toFixed(2)}</Typography>
                        </Paper>
                    </Grid>
                    <Grid item xs={6} sm={4} md={2}>
                        <Paper elevation={1} sx={{ p: 1.5, textAlign: 'center', bgcolor: 'background.default' }}>
                            <Typography variant="caption" color="text.secondary" fontWeight={600}>RMS</Typography>
                            <Typography variant="h6" color="primary.main">{metrics.rms.toFixed(2)}</Typography>
                        </Paper>
                    </Grid>
                    <Grid item xs={6} sm={4} md={2}>
                        <Paper elevation={1} sx={{ p: 1.5, textAlign: 'center', bgcolor: 'background.default' }}>
                            <Typography variant="caption" color="text.secondary" fontWeight={600}>Crest Factor</Typography>
                            <Typography variant="h6" color="primary.main">{metrics.crestFactor.toFixed(2)}</Typography>
                        </Paper>
                    </Grid>
                    <Grid item xs={6} sm={4} md={2}>
                        <Paper elevation={1} sx={{ p: 1.5, textAlign: 'center', bgcolor: 'background.default' }}>
                            <Typography variant="caption" color="text.secondary" fontWeight={600}>Kurtosis</Typography>
                            <Typography variant="h6" color="primary.main">{metrics.kurtosis.toFixed(2)}</Typography>
                        </Paper>
                    </Grid>
                </Grid>
            )}
            <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                    <Box sx={{ height: 300 }}>
                        <CanvasJSChart options={chartOptions.acc} />
                    </Box>
                </Grid>
                <Grid item xs={12} md={6}>
                    <Box sx={{ height: 300 }}>
                        <CanvasJSChart options={chartOptions.telemetry} />
                    </Box>
                </Grid>
                <Grid item xs={12} md={6}>
                    <Box sx={{ height: 300 }}>
                        <CanvasJSChart options={chartOptions.magHist} />
                    </Box>
                </Grid>
                <Grid item xs={12} md={6}>
                    <Box sx={{ height: 300 }}>
                        <CanvasJSChart options={chartOptions.gyro} />
                    </Box>
                </Grid>
            </Grid>
        </Box>
    );
}

function createHistogram(data: Float32Array, bins: number) {
    let min = Infinity, max = -Infinity;
    for (let i = 0; i < data.length; i++) {
        if (data[i] < min) min = data[i];
        if (data[i] > max) max = data[i];
    }
    const binSize = (max - min) / bins;
    const freqs = new Array(bins).fill(0);

    for (let i = 0; i < data.length; i++) {
        let idx = Math.floor((data[i] - min) / binSize);
        if (idx === bins) idx--;
        freqs[idx]++;
    }

    const dps = [];
    for (let i = 0; i < bins; i++) {
        const binStart = min + i * binSize;
        const binEnd = min + (i + 1) * binSize;
        dps.push({ label: `${binStart.toFixed(1)} - ${binEnd.toFixed(1)}`, y: freqs[i] });
    }
    return dps;
}
